/** The order-rate panel: token-bucket saturation and the exits' reserve (WC-02, U060). */
import type { OrderRateRow } from "../../contracts/ops.ts";
import { badge, definitions, instantCell } from "../../app/components.ts";
import { h, type Child } from "../../app/dom.ts";

/** What the telemetry says went wrong, if anything: a breached limit or starved exits. */
export function rateBreaches(row: OrderRateRow): readonly string[] {
  const breaches: string[] = [];
  if (row.peakPerSecond > row.perSecondLimit) {
    breaches.push(
      `Per-second limit exceeded: peak ${String(row.peakPerSecond)} against ${String(row.perSecondLimit)} orders/s.`,
    );
  }
  if (row.sentLastMinute > row.perMinuteLimit) {
    breaches.push(
      `Per-minute limit exceeded: ${String(row.sentLastMinute)} against ${String(row.perMinuteLimit)} orders/min.`,
    );
  }
  if (row.exitsStarved > 0) {
    breaches.push(
      `Exits were starved ${String(row.exitsStarved)} time(s): the exits' reserve of ${String(row.exitReservePerSecond)} orders/s was breached.`,
    );
  }
  return breaches;
}

/** The panel's content for one telemetry row. */
export function orderRateContent(row: OrderRateRow): readonly Child[] {
  const breaches = rateBreaches(row);
  const saturated = row.saturationEvents > 0;
  return [
    breaches.length === 0
      ? h(
          "p",
          { attrs: { role: "status", "data-rate": "within" } },
          badge("Within limits", "ok"),
          saturated
            ? ` The bucket saturated ${String(row.saturationEvents)} time(s); entries were held back and exits kept their reserve.`
            : " The bucket has not saturated.",
        )
      : h(
          "div",
          { class: "breach", attrs: { role: "alert", "data-rate": "breach" } },
          ...breaches.map((text) => h("p", {}, text)),
        ),
    definitions([
      ["Per-second limit", `${String(row.perSecondLimit)} orders/s`],
      ["Per-minute limit", `${String(row.perMinuteLimit)} orders/min`],
      [
        "Exits' reserve",
        `${String(row.exitReservePerSecond)} orders/s kept for cancels and risk reduction`,
      ],
      ["Modification cap", `${String(row.modificationCap)} per order`],
      ["Peak in one second", `${String(row.peakPerSecond)} of ${String(row.perSecondLimit)}`],
      ["Sent in the last minute", `${String(row.sentLastMinute)} of ${String(row.perMinuteLimit)}`],
      ["Bucket tokens", `${String(row.bucketTokens)} of ${String(row.bucketCapacity)}`],
      ["Saturation events", String(row.saturationEvents)],
      ["Last saturated", instantCell(row.lastSaturatedAt)],
      ["Entries denied", String(row.entriesDenied)],
      ["Exits denied", String(row.exitsDenied)],
      ["Exits starved", String(row.exitsStarved)],
      ["Measured", instantCell(row.measuredAt)],
    ]),
  ];
}
